import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, Building2, MapPin, Search, Star, Lightbulb, Plus, RefreshCw, ChevronRight, AlertCircle, CheckCircle2 } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import { BottomNav } from '../components/BottomNav';

const priorityColors = { critical: 'bg-red-100 text-red-700 border-red-200', high: 'bg-orange-100 text-orange-700 border-orange-200', medium: 'bg-blue-100 text-blue-700 border-blue-200', low: 'bg-gray-100 text-gray-600 border-gray-200' };

export const GrowthDashboardScreen = () => {
  const navigate = useNavigate();
  const [business, setBusiness] = useState(null);
  const [score, setScore] = useState(null);
  const [recs, setRecs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [noBusiness, setNoBusiness] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const bizRes = await api.get('/businesses');
      const businesses = bizRes.data.businesses || [];
      if (businesses.length === 0) { setNoBusiness(true); setLoading(false); return; }
      const biz = businesses[0];
      setBusiness(biz);
      const [scoreRes, recRes] = await Promise.all([
        api.get(`/businesses/${biz.id}/growth-score`),
        api.get(`/businesses/${biz.id}/growth-recommendations`)
      ]);
      setScore(scoreRes.data.score);
      setRecs(recRes.data.recommendations || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const handleRecalculate = async () => {
    if (!business) return;
    try {
      const res = await api.post(`/businesses/${business.id}/growth-score/recalculate`);
      setScore(res.data.score);
      toast.success('Growth score updated');
      const recRes = await api.get(`/businesses/${business.id}/growth-recommendations`);
      setRecs(recRes.data.recommendations || []);
    } catch { toast.error('Failed to recalculate'); }
  };

  // Onboarding
  if (noBusiness) return (
    <div className="min-h-screen bg-[#F0F2F5] pb-20">
      <div className="w-full max-w-md mx-auto bg-white min-h-screen shadow-xl">
        <div className="bg-[#128C7E] text-white px-4 py-4"><h1 className="text-lg font-bold">Growth Engine</h1></div>
        <div className="p-6 text-center">
          <div className="w-20 h-20 bg-gradient-to-br from-emerald-100 to-teal-100 rounded-full flex items-center justify-center mx-auto mb-4"><TrendingUp size={32} className="text-[#128C7E]" /></div>
          <h2 className="text-xl font-bold text-slate-800 mb-2">Let's Set Up Your Business</h2>
          <p className="text-sm text-gray-500 mb-6">Create your business profile to start tracking growth opportunities.</p>
          <button onClick={() => navigate('/more/business-profile/new')} className="w-full h-14 bg-[#128C7E] text-white rounded-xl text-base font-bold active:scale-95 transition-transform" data-testid="setup-business-btn">Setup Business Profile</button>
        </div>
      </div>
      <BottomNav />
    </div>
  );

  const s = score?.total_score || 0;
  const dims = score?.dimensions || [];

  return (
    <div className="min-h-screen bg-[#F0F2F5] pb-20">
      <div className="w-full max-w-md mx-auto bg-white min-h-screen shadow-xl">
        <div className="bg-[#128C7E] text-white px-4 py-4 flex items-center justify-between">
          <div><h1 className="text-lg font-bold">Growth Engine</h1><p className="text-[10px] opacity-80">{business?.business_name || 'Your Business'}</p></div>
          <button onClick={handleRecalculate} className="p-2 bg-white/20 rounded-lg" data-testid="growth-recalculate"><RefreshCw size={16} /></button>
        </div>

        <div className="p-4 space-y-4">
          {/* Growth Score */}
          <div className="bg-gradient-to-br from-[#128C7E] to-[#075E54] rounded-2xl p-5 text-white text-center" data-testid="growth-score">
            <p className="text-xs opacity-70 mb-1">Your Growth Score</p>
            <div className="relative w-24 h-24 mx-auto my-2">
              <svg className="w-24 h-24 -rotate-90" viewBox="0 0 36 36">
                <path className="text-white/20" stroke="currentColor" strokeWidth="3" fill="none" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                <path className="text-white" stroke="currentColor" strokeWidth="3" fill="none" strokeDasharray={`${s}, 100`} d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center"><span className="text-2xl font-bold">{s}</span></div>
            </div>
            <p className="text-sm font-semibold">{s >= 75 ? 'Strong' : s >= 60 ? 'Growing' : s >= 40 ? 'Needs Attention' : 'Getting Started'}</p>
          </div>

          {/* Priority Actions */}
          {recs.length > 0 && (
            <div>
              <h3 className="text-sm font-bold text-slate-700 mb-2 flex items-center gap-1"><Lightbulb size={14} className="text-amber-500" /> Priority Actions</h3>
              <div className="space-y-2">
                {recs.slice(0, 4).map((r, i) => (
                  <button key={i} onClick={() => r.action_payload?.route && navigate(r.action_payload.route)}
                    className={`w-full flex items-center gap-3 p-3 rounded-xl border text-left active:scale-[0.98] transition-transform ${priorityColors[r.priority] || priorityColors.medium}`}
                    data-testid={`rec-${i}`}
                  >
                    <AlertCircle size={16} className="flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold">{r.title}</p>
                      <p className="text-[10px] opacity-70 mt-0.5 truncate">{r.description}</p>
                    </div>
                    <ChevronRight size={14} className="flex-shrink-0 opacity-50" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Score Breakdown */}
          <div>
            <h3 className="text-sm font-bold text-slate-700 mb-2">Growth Breakdown</h3>
            <div className="space-y-2">
              {dims.map((d, i) => (
                <div key={i} className="bg-white border border-gray-100 rounded-xl p-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-semibold text-slate-700">{d.name}</span>
                    <span className="text-xs font-bold text-[#128C7E]">{d.score}/{d.max_score}</span>
                  </div>
                  <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full transition-all ${d.status === 'excellent' ? 'bg-emerald-500' : d.status === 'good' ? 'bg-[#128C7E]' : d.status === 'needs_improvement' ? 'bg-amber-500' : 'bg-red-500'}`}
                      style={{ width: `${(d.score / d.max_score) * 100}%` }} />
                  </div>
                  <p className="text-[10px] text-gray-500 mt-1 capitalize">{(d.status || '').replace('_', ' ')}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div className="space-y-2">
            {/* Local SEO Card */}
            <button onClick={() => navigate('/more/local-seo')} className="w-full bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 rounded-xl p-4 text-left active:scale-[0.98] transition-transform" data-testid="growth-local-seo">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Search size={18} className="text-blue-600" />
                  <div>
                    <p className="text-sm font-bold text-blue-800">Local SEO</p>
                    <p className="text-[10px] text-blue-600">
                      {(dims.find(d => d.name === 'Local SEO') || {}).score || 0}/{(dims.find(d => d.name === 'Local SEO') || {}).max_score || 20}
                    </p>
                  </div>
                </div>
                <ChevronRight size={16} className="text-blue-400" />
              </div>
            </button>

            {/* Reputation Card */}
            <button onClick={() => navigate('/more/reputation')} className="w-full bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-100 rounded-xl p-4 text-left active:scale-[0.98] transition-transform" data-testid="growth-reputation">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Star size={18} className="text-amber-600" />
                  <div>
                    <p className="text-sm font-bold text-amber-800">Reputation</p>
                    <p className="text-[10px] text-amber-600">
                      {(dims.find(d => d.name === 'Reviews & Reputation') || {}).score || 0}/{(dims.find(d => d.name === 'Reviews & Reputation') || {}).max_score || 10}
                    </p>
                  </div>
                </div>
                <ChevronRight size={16} className="text-amber-400" />
              </div>
            </button>
          </div>

          {/* More Quick Links */}
          <div className="grid grid-cols-2 gap-2">
            {[
              { icon: Building2, label: 'Business Profile', route: '/more/business-profile' },
              { icon: MapPin, label: 'Locations', route: '/more/business-profile' },
            ].map(q => (
              <button key={q.label} onClick={() => navigate(q.route)}
                className="flex items-center gap-2 p-3 bg-gray-50 rounded-xl text-left active:scale-95 transition-transform"
                data-testid={`growth-${q.label.toLowerCase().replace(/\s/g, '-')}`}
              >
                <q.icon size={16} className="text-[#128C7E]" />
                <span className="text-xs font-semibold text-slate-700">{q.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
      <BottomNav />
    </div>
  );
};
